/**
 * Les lignes d'effets d'un objet, à la manière de l'infobulle du jeu :
 * `15 Vitalité` en clair, et la fourchette de l'objet en petit à côté.
 * Partagé par la fiche d'équipement et l'aperçu d'un craft.
 */
import { detailJet, effetLisible, type Porte } from "../sim/objets.ts"
import { item as parId } from "../sim/contenu.ts"
import type { Contenu } from "../sim/contenu.ts"
import { esc } from "./fmt.ts"
import { ic } from "./sprites.ts"

/** Les cinq éléments primeront sur « résistance » dans `% Résistance Feu`. */
const ELEMENTS = ["terre", "feu", "eau", "air", "neutre"]
const AUTRES = ["vitalite", "sagesse", "force", "intelligence", "chance", "agilite",
  "puissance", "prospection", "critique", "initiative", "dommages", "soin", "tacle",
  "fuite", "invocation", "erosion", "resistance", "pod", "pa", "pm", "po"]

/** `% Résistance Feu` → `feu` ; `- Force` → `force` ; sinon rien. */
function cleIcone(stat: string): string {
  const n = stat.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  const mot = (k: string) => new RegExp(`(^|[^a-z])${k}([^a-z]|$)`).test(n)
  return ELEMENTS.find(mot) ?? AUTRES.find(mot) ?? ""
}

/**
 * Un effet fixe n'affiche pas de fourchette : il n'y a rien à comparer.
 * Un jet au maximum se signale en vert, au minimum en rouge, les deux seuls
 * cas que le joueur remarque vraiment.
 */
/**
 * Les effets d'un objet **avant** de le fabriquer : la fourchette seule, sans
 * valeur tirée. On ne pouvait juger une recette qu'après l'avoir faite, alors
 * que c'est justement ce qu'on veut savoir pour décider.
 */
export function lignesFourchette(c: Contenu, item: number): string {
  const it = parId(c, item)
  if (!it) return ""
  const utiles = it.effets.filter((e: { stat: string }) => effetLisible(e.stat))
  if (utiles.length === 0) return ""
  return `<ul class="jetl fourchette">${utiles.map((e: { stat: string; min: number; max: number }) => `
    <li>
      <span class="v">${e.min === e.max ? e.min : `${e.min}–${e.max}`}</span>
      <span class="n">${ic(cleIcone(e.stat), 16)}${esc(e.stat)}</span>
    </li>`).join("")}</ul>`
}

export function lignesJet(c: Contenu, p: Porte): string {
  return `<ul class="jetl">${detailJet(c, p).map((e) => {
    const cl = e.fixe ? "" : e.valeur >= e.max ? " max" : e.valeur <= e.min ? " min" : ""
    return `<li>
      <span class="v${cl}">${e.valeur}</span>
      <span class="n">${ic(cleIcone(e.stat), 16)}${esc(e.stat)}</span>
      ${e.fixe ? "" : `<em>${e.min}–${e.max}</em>`}
    </li>`
  }).join("")}</ul>`
}
